import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { JobStatusStyledIcon } from '@/console/features/jobs/components/job-status-styled-icon/job-status-styled-icon';
import { TTargetMenuItemProps } from '@/console/features/pipelines/components/target-menu-item/target-menu-item.types';
import { useFeatureV2 } from '@/console/features/pipelines/hooks/use-feature/use-feature-v2';
import { TGetFeatureProps } from '@/console/features/pipelines/hooks/use-feature/use-feature.types';
import { usePipelineStepType } from '@/console/features/pipelines/hooks/use-pipeline-step-type/use-pipeline-step-type';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { LicenseIcon } from '@/shared/assets/icons/license-icon';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useReadDeviceQuery } from '@/shared/redux/api/assets/devices/devices-api';
import { useReadProjectQuery } from '@/shared/redux/api/assets/projects/projects-api';
import { FeatureTypeEnum } from '@/shared/types/types.generated';

export const TargetMenuItem: React.FC<TTargetMenuItemProps> = ({
  jobType,
  target,
  ...rest
}) => {
  const { t } = useTranslation();
  const { getStepTypeLabel } = usePipelineStepType();
  const { projectIds, deviceIds } = target;
  const { data: projectData, isLoading: isLoadingProject } =
    useReadProjectQuery(
      { projectId: projectIds?.[0]! },
      {
        skip: !projectIds?.length,
      },
    );
  const { data: deviceData, isLoading: isLoadingDevice } = useReadDeviceQuery(
    { deviceId: deviceIds?.[0]! },
    {
      skip: !deviceIds?.length,
    },
  );

  const project = projectData?.readProject;
  const device = deviceData?.readDevice;

  const { getFeature } = useFeatureV2();
  const feature = getFeature({
    feature: jobType,
    ...(device || project),
  } as TGetFeatureProps);
  const featureType = feature?.metaData.featureType;

  const isLoading = isLoadingProject || isLoadingDevice;

  const renderIcon = () => {
    if (featureType === FeatureTypeEnum.DeviceFeature) {
      return <DevicesIcon />;
    }

    if (featureType === FeatureTypeEnum.LicenseFeature) {
      return <LicenseIcon />;
    }

    return <ProjectsIcon />;
  };

  const renderLabel = () => {
    if (featureType === FeatureTypeEnum.DeviceFeature) {
      return isLoading ? t('ui:loading') : device?.name;
    }

    if (featureType === FeatureTypeEnum.LicenseFeature) {
      return getStepTypeLabel(jobType);
    }

    return isLoading ? t('ui:loading') : project?.name;
  };

  const renderStatusIcon = () => {
    if (target.__typename === 'TargetExecutionStatusResponseType') {
      return <JobStatusStyledIcon status={target.status} />;
    }
    return null;
  };

  if (!isLoading && !projectData && !deviceData) {
    return null;
  }

  return (
    <MenuItem icon={renderStatusIcon()} {...rest}>
      <Stack gap="0.375rem" direction="row" alignItems="center">
        {renderIcon()}
        {renderLabel()}
      </Stack>
    </MenuItem>
  );
};
